import { clearOutboxSnapshot, outboxOwner } from './store';
import { outboxStorage, outboxStorageSupported } from './storage';
import type { OutboxMessage } from './types';

/**
 * Deleting the Outbox, and counting what is in it first.
 *
 * ## Why this is not just another purge
 *
 * `purgeOfflineCache` (ADR 0059) and the service worker's API cache both delete
 * *copies* of mail the server still has. This deletes **originals**: a queued
 * message is mail the user wrote and the server has never seen, and this device
 * holds the only copy of it (ADR 0058). Nothing here may run without the user
 * having been told — the question lives in the session layer
 * (`app/session.tsx` + `components/SignOutOutboxDialog.tsx`), and this module
 * is the hands, not the decision.
 *
 * ## Why the whole database, not `clear()`
 *
 * Same argument as ADR 0059 §"One database, one fixed name": one
 * `deleteDatabase('ols-mail-outbox')`, no enumeration, nothing of any account
 * missed because the whole file is gone. `clear()` empties the one store this
 * version happens to have; a store added in a future version would survive it.
 *
 * ## Only a confirmed deletion counts
 *
 * A database another tab still holds open reports `'failed'`, never `'purged'`
 * — see `OutboxStorage.destroy`. Sign-out then says so out loud
 * (`reportCachePurge`), because "you are signed out" reads as "your mail is off
 * this machine" and in that case it is not.
 */

/**
 * Why the Outbox is or is not gone. Every value but `'purged'` means unsent
 * mail may still be on this disk.
 */
export type OutboxPurgeOutcome =
  | 'purged'
  /** This browser has no usable IndexedDB, so no outbox was ever written. */
  | 'unsupported'
  /** Asked, and the deletion did not happen — another tab still holds it open. */
  | 'failed';

export interface OutboxPurge {
  outcome: OutboxPurgeOutcome;
  /**
   * How many unsent messages the deletion destroyed, counted immediately
   * before it. `0` on the common path, where the queue was empty and there was
   * nothing to lose.
   */
  discarded: number;
}

/**
 * How many unsent messages this device is holding, read from disk.
 *
 * Read from storage rather than from `outboxSnapshot()`, deliberately: the
 * snapshot is populated lazily by the first `useSyncExternalStore` subscriber,
 * so a tab that has not drawn the Outbox badge yet would answer `0` for a queue
 * that is really there — and that answer would be used to skip the question
 * and delete the messages without asking.
 *
 * A database that cannot be read answers `0`. That is not a guess: a queue the
 * app cannot read is one the user cannot see, send, edit or recover either, so
 * there is nothing to hold a sign-out for and nothing to describe in a prompt.
 * It is still deleted by {@link purgeOutbox}, which is the half that matters
 * for the person who uses this machine next.
 */
export async function queuedOutboxCount(): Promise<number> {
  if (!outboxStorageSupported()) return 0;
  try {
    return (await outboxStorage().all()).filter(ownedBySignedIn).length;
  } catch {
    return 0;
  }
}

/**
 * The signed-in owner's records, plus any that predate owner stamping.
 *
 * Everything else on disk is another owner's queue, kept after *their*
 * session was revoked (ADR 0061 §4) so it can send when they sign back in.
 * This session's sign-out neither asks about it nor deletes it.
 */
function ownedBySignedIn(record: OutboxMessage): boolean {
  return record.ownerId === undefined || record.ownerId === outboxOwner();
}

/**
 * Delete the Outbox database, and report what went with it.
 *
 * Deletes the signed-in owner's queue — the whole database when nothing else
 * is in it, which is the ordinary case. Another owner's queue, kept after
 * their session was revoked (ADR 0061 §4), is left where it is.
 *
 * Unconditional by design. The protection for the user's unsent mail is the
 * question asked *before* sign-out gets this far, not a check in here: a
 * teardown that reached this point on a cross-tab `logout` has a decision the
 * user already made in the tab they made it in, and one that reached it on an
 * identity change has a different person already signed in on this tab.
 */
export async function purgeOutbox(): Promise<OutboxPurge> {
  if (!outboxStorageSupported()) return { outcome: 'unsupported', discarded: 0 };

  // Counted before the delete, because afterwards there is nothing left to
  // count and "how many did I just lose" is the one fact worth reporting.
  let all: OutboxMessage[] | undefined;
  try {
    all = await outboxStorage().all();
  } catch {
    all = undefined;
  }
  const mine = all?.filter(ownedBySignedIn) ?? [];
  const othersKept = (all?.length ?? 0) - mine.length;
  const discarded = mine.length;

  let outcome: OutboxPurgeOutcome;
  if (othersKept === 0) {
    // The common case, and ADR 0061 §1 exactly: the whole file goes.
    try {
      outcome = (await outboxStorage().destroy()) ? 'purged' : 'failed';
    } catch {
      outcome = 'failed';
    }
  } else {
    // Another owner's kept queue is on this disk (ADR 0061 §4). It is not this
    // session's to destroy, so only this owner's records go, one by one.
    try {
      for (const record of mine) await outboxStorage().remove(record.id);
      outcome = 'purged';
    } catch {
      outcome = 'failed';
    }
  }

  // Unconditional, including after a failure: this tab is on its way to the
  // login screen either way, and a snapshot left populated is the previous
  // user's recipients and subjects still drawn in the UI.
  //
  // What this does NOT promise is that the name never appears in
  // `indexedDB.databases()` again. The adapter opens the database per
  // operation, so a flusher tick still in flight during the navigation to
  // /login — or the next sign-in on this same page — re-creates it, and a
  // browser observing across the sign-out sees it vanish and come back. That
  // is an empty file with an empty object store: `add` is reached only from
  // the composer, and every other write is a `mutate` that no-ops on a record
  // that is no longer there, so nothing of the previous user can return to it.
  // Latching the storage closed instead would buy a tidier listing at the
  // price of a reopen rule to get wrong, on the path where signing back in on
  // the same page has to keep working.
  clearOutboxSnapshot();

  return { outcome, discarded };
}
